/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import React, { useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import CircularProgress from "@material-ui/core/CircularProgress";
import AddToCartIcon from "@material-ui/icons/AddShoppingCart";
import axios from "axios";

import CartIcon from "@material-ui/icons/ShoppingCart";
import Switch from "@material-ui/core/Switch";

import Cart from "./tools/Cart";
import Price from "./tools/Price";
import confirm from "./Dialog";
import { useStoreon } from "storeon/react";

import t from "../translations";
import { useBackendPrice } from "utils/price";
import { useProboAmount } from "utils/probo";
import { clone } from "underscore";
import { updateOrderItemDesign, addToCart } from "api";
import { query } from "utils/general";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100% - 64px);
  flex-wrap: unset;
  padding: 10px 30px;
  @media screen and (max-width: 480px) {
    padding: 0 10px;
  }
  .footer,
  .header {
    padding: 10px 0;
  }
  > .footer {
    flex-basis: 130px;
    display: flex;
    flex-direction: row;
    @media screen and (max-width: 640px) {
      flex-direction: column;
      flex-basis: 400px;
    }
    > .license {
      background: #f4f4f4;
      color: #333;
      padding: 10px;
      font-size: 13px;
      position: relative;
      text-align: justify;
      padding-right: 60px;
      min-width: calc(100% - 320px);
      @media screen and (max-width: 640px) {
        padding-right: 10px;
      }
      @media screen and (max-width: 480px) {
        font-size: 10px;
      }
      .switch {
        position: absolute;
        right: 0px;
        bottom: 0px;
        margin: 0;
      }
    }
    > .tocart {
      min-width: 320px;
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      padding: 7px;
      /* @media screen and (max-width: 640px) { */
      /* } */
    }
  }
  .header {
    flex-basis: 50px;
    display: flex;
    align-items: flex-end;
    align-self: start;
    > span {
      margin-right: 15px;
    }
  }
  > .preview {
    flex-basis: 100%;
    max-height: calc(100%);
    height: calc(100% - 100px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    min-width: 0;
    > img {
      object-fit: contain;
      height: calc(100% - 70px);
      max-width: 100%;
    }
  }
`;

const CountInput = styled.input`
  width: 50px;
  height: ${(props) => (props.disabled ? 30 : 32)}px;
  color: ${(props) => (props.disabled ? "#e0e0e0" : "#333")};
  text-align: center;
  outline: 3px solid ${(props) => (props.disabled ? "#e0e0e0" : "#a8bb43")};
  font-size: 18px;
  font-weight: bold;
  margin-right: -4px;
  z-index: 1;
  border: none;
  margin-bottom: ${(props) => (props.disabled ? 3 : 1)}px;
`;

function FullScreenDialog() {
  const {
    dispatch,
    product,
    preview,
    canvas,
    contentId,
    contentType,
    cart,
    proboCodes,
  } = useStoreon(
    "preview",
    "canvas",
    "cart",
    "product",
    "proboCodes",
    "contentId",
    "contentType"
  );
  const proboAmount = useProboAmount();
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [count, setCount] = useState(proboAmount);
  const [isAccepted, setIsAccepted] = useState(false);
  const { total } = useBackendPrice();

  useEffect(() => {
    setCount(proboAmount);
  }, [proboAmount]);

  const showPreview = !!preview.isOpen;
  useEffect(() => {
    if (contentType === "orderItem") {
      setCount(window.DeskpadData.count);
    }
  }, [showPreview, contentType]);

  function handleClose() {
    dispatch("preview/hide");
    setIsAccepted(false);
  }

  const getCount = () => {
    let parsedVal = parseInt(count);
    if (parsedVal > 99) {
      parsedVal = 99;
    }
    if (parsedVal <= 0 || !parsedVal) {
      parsedVal = 1;
    }
    return parsedVal;
  };

  const handleCountChange = (e) => {
    const val = e.target.value;
    setCount(val);
  };

  const handleCountBlur = (e) => {
    setCount(getCount());
  };

  useEffect(() => {
    if (product?.proboProduct) {
      const codes = clone(proboCodes);
      const amount = codes.find((opt) => opt.code === "amount");
      console.log("updating probo amount", codes, getCount());
      if (amount) {
        amount.value = getCount();
        dispatch("product/setProboCodes", codes);
      }
    }
  }, [count]);

  const addUpdateCart = async () => {
    setIsCartLoading(true);
    const params = {
      canvasData: canvas.DPE.persistent.toBase64(),
      width: canvas.cutW,
      height: canvas.cutH,
      productId: product?.id,
      count: getCount(),
      cartId: contentId,
      proboProductOptions: proboCodes,
    };

    try {
      const data = await addToCart(params);
      if (data.cart) {
        dispatch("cart/set", data.cart);
      }
      if (data.key) {
        dispatch("preview/hide");
        setIsAccepted(false);
        dispatch("notification/success", t.prodcutAddedSuccessful);
        canvas.DPE.persistent.clearStorage();

        const result = await confirm.show({
          icon: <CartIcon style={{ marginRight: 10 }} />,
          yes: t.goToCart,
          no: t.oneMore,
          text: t.cartOrNew,
          title: t.itemAddedToCart,
        });
        if (!result) {
          dispatch("products/designMore");
        } else {
          document.location.href = cart.url;
        }
      }
      window._paq?.push([
        "trackEvent",
        "Cart",
        "Added to cart",
        product?.title,
        total,
      ]);
    } catch (err) {
      dispatch("notification/error", { text: "Cart update failed", err });
    }
    setIsCartLoading(false);
  };

  const updateOrderItem = async () => {
    setIsCartLoading(true);
    const params = {
      canvasData: canvas.DPE.persistent.toBase64(),
      width: canvas.cutW,
      height: canvas.cutH,
      orderItemId: contentId,
      productId: product?.id,
      count: getCount(),
      orderId: query.get("orderId"),
      proboOptions: proboCodes,
    };
    try {
      const data = await updateOrderItemDesign(params);
      if (data.cart) {
        dispatch("cart/set", data.cart);
      }
      if (data.orderItemId) {
        dispatch("preview/hide");
        setIsAccepted(false);
        dispatch("notification/success", "Order updated");
        const getOrderIdParam = new URLSearchParams(window.location.search).get(
          "orderId"
        );
        localStorage.setItem("updateDpeOrder_" + getOrderIdParam, "1");
        window.close();
      }
    } catch (err) {
      dispatch("notification/error", { text: "Order update failed", err });
    }
    setIsCartLoading(false);
  };

  const handleAddToCartClick = () => {
    if (isCartLoading) return;
    if (contentType === "orderItem") {
      updateOrderItem();
    } else {
      addUpdateCart();
    }
  };

  return (
    <Dialog
      style={{ zIndex: 99999 }}
      fullScreen
      open={!!preview.isOpen}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar
        position="relative"
        style={{
          boxShadow: "none",
          background: "#262626",
        }}
      >
        <Toolbar>
          <IconButton
            style={{ color: "white" }}
            edge="start"
            onClick={handleClose}
            aria-label={t.close}
            css={css`
              transition: 0.2s ease all;
              :hover {
                background: #fff3;
                transform: scale(1.1);
              }
              @media screen and (max-width: 480px) {
                padding-left: 0px;
                margin-left: -10px;
              }
            `}
          >
            <CloseIcon
              css={css`
                width: 35px;
                height: 35px;
              `}
            />
          </IconButton>
          <Typography variant="h6" style={{ color: "white" }}>
            {t.preview}
          </Typography>
          <Typography component="p" style={{ flexGrow: 1 }} />
          <Cart />
          <Price count={count} />
        </Toolbar>
      </AppBar>
      <PreviewWrap>
        <div className="preview">
          <div className="header">
            <span>
              <b>{t.size}: </b>
              {canvas &&
                `${(canvas.cutW / 10).toFixed(0)}x${(canvas.cutH / 10).toFixed(
                  0
                )}cm`}
            </span>
          </div>
          {!preview.isLoading ? (
            <img src={preview.url} alt="Deskpad" />
          ) : (
            <div
              css={css`
                height: calc(100% - 33px);
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <CircularProgress size={60} color="secondary" />
            </div>
          )}
        </div>
        <div className="footer">
          <div className="license">
            <b>{t.note}</b>
            <br />
            <div
              dangerouslySetInnerHTML={{
                __html: window.DeskpadContent.license
                  .replace("[title]", product?.title ?? "")
                  .replace("[delivery-info]", product?.deliveryInfo ?? ""),
              }}
            />
          </div>
          <div className="tocart">
            <div>
              <Switch
                checked={isAccepted}
                onChange={(_) => {
                  setIsAccepted(!isAccepted);
                }}
                value="checkedA"
              />
            </div>
            {!product?.proboProduct && (
              <CountInput
                value={count}
                onChange={handleCountChange}
                onBlur={handleCountBlur}
                type="number"
                disabled={!isAccepted}
              />
            )}
            <Button
              disabled={!isAccepted}
              color="secondary"
              variant="contained"
              onClick={handleAddToCartClick}
            >
              {isCartLoading ? (
                <CircularProgress
                  size={24}
                  color="primary"
                  style={{ marginRight: 5, color: "white" }}
                />
              ) : (
                <AddToCartIcon style={{ marginRight: 5 }} />
              )}
              {contentType === "orderItem" && "Update order"}
              {contentType !== "orderItem" &&
                (contentId && contentType === "cartItem"
                  ? t.updateCart
                  : t.addToCart)}
            </Button>
          </div>
        </div>
      </PreviewWrap>
    </Dialog>
  );
}

export default FullScreenDialog;
